import { useState, useCallback, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Fab,
  Container,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { useFlocks, useArchiveFlock } from '../features/flocks/hooks/useFlocks';
import { useCoopDetail } from '../features/coops/hooks/useCoops';
import { CreateFlockModal } from '../features/flocks/components/CreateFlockModal';
import { EditFlockModal } from '../features/flocks/components/EditFlockModal';
import { ArchiveFlockDialog } from '../features/flocks/components/ArchiveFlockDialog';
import { FlockCard } from '../features/flocks/components/FlockCard';
import { FlocksEmptyState } from '../features/flocks/components/FlocksEmptyState';
import { FlockCardSkeleton } from '../shared/components/FlockCardSkeleton';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useToast } from '../hooks/useToast';
import { processApiError } from '../lib/errors';
import type { Flock } from '../features/flocks/api/flocksApi';
import { useUserSettingsContext } from '../features/settings';

export default function FlocksPage() {
  const { t } = useTranslation();
  const { singleCoopMode } = useUserSettingsContext();
  const { coopId } = useParams<{ coopId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [includeInactive, setIncludeInactive] = useState(false);
  const { data: flocks, isLoading, error, refetch } = useFlocks(coopId!, includeInactive);
  const { data: coop } = useCoopDetail(coopId!);
  const { mutate: archiveFlock, isPending: isArchiving } = useArchiveFlock();
  const { handleError } = useErrorHandler();
  const { showSuccess } = useToast();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [selectedFlock, setSelectedFlock] = useState<Flock | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Auto-open create modal when navigated with ?addFlock=true
  useEffect(() => {
    if (searchParams.get('addFlock') === 'true') {
      setIsCreateModalOpen(true);
      // Remove the query param without re-rendering the whole page
      navigate({ search: '' }, { replace: true });
    }
  }, [searchParams, navigate]);

  // Pull-to-refresh handler
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } catch (err) {
      handleError(err, handleRefresh);
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch, handleError]);

  // Edit flock handler
  const handleEdit = (flock: Flock) => {
    setSelectedFlock(flock);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedFlock(null);
  };

  // Archive flock handlers
  const handleArchive = (flock: Flock) => {
    setSelectedFlock(flock);
    setIsArchiveDialogOpen(true);
  };

  const handleCloseArchiveDialog = () => {
    setIsArchiveDialogOpen(false);
    setSelectedFlock(null);
  };

  const handleConfirmArchive = () => {
    if (!selectedFlock) return;

    archiveFlock(
      { coopId: selectedFlock.coopId, flockId: selectedFlock.id },
      {
        onSuccess: () => {
          showSuccess(t('flocks.archiveSuccess'));
          setIsArchiveDialogOpen(false);
          setSelectedFlock(null);
        },
        onError: (error: Error) => {
          setIsArchiveDialogOpen(false);
          setSelectedFlock(null);
          handleError(error, handleConfirmArchive);
        },
      }
    );
  };

  // Sort flocks by created date (newest first)
  const sortedFlocks = flocks
    ? [...flocks].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    : [];

  // Filter by active/inactive status
  const filteredFlocks = sortedFlocks.filter(flock =>
    includeInactive ? true : flock.isActive
  );

  if (!coopId) {
    return (
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          textAlign: 'center',
          py: 3,
        }}
      >
        <Typography variant="h6" color="error">
          {t('common.error')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('errors.missingCoopId')}
        </Typography>
      </Container>
    );
  }

  if (error) {
    const processedError = processApiError(error);

    return (
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          textAlign: 'center',
          py: 3,
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          {t(processedError.translationKey)}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {processedError.message}
        </Typography>

        {processedError.canRetry && (
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            {t('common.retry')}
          </Button>
        )}
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{ py: 3 }}
      onTouchStart={(e) => {
        const touch = e.touches[0];
        const startY = touch.clientY;
        const onTouchMove = (moveEvent: TouchEvent) => {
          const moveTouch = moveEvent.touches[0];
          const deltaY = moveTouch.clientY - startY;
          if (deltaY > 100 && window.scrollY === 0 && !isRefreshing) {
            handleRefresh();
            document.removeEventListener('touchmove', onTouchMove);
          }
        };
        document.addEventListener('touchmove', onTouchMove);
        document.addEventListener('touchend', () => {
          document.removeEventListener('touchmove', onTouchMove);
        }, { once: true });
      }}
    >
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('flocks.title')}
        </Typography>
        {!singleCoopMode && coop?.name && (
          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: -1, mb: 1 }}>
            {coop.name}
          </Typography>
        )}

        {/* Filter toggle */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <ToggleButtonGroup
            value={includeInactive ? 'all' : 'active'}
            exclusive
            onChange={(_, newValue) => {
              if (newValue !== null) {
                setIncludeInactive(newValue === 'all');
              }
            }}
            size="small"
            color="primary"
            aria-label={t('flocks.filterStatus')}
            sx={{
              '& .MuiToggleButton-root': {
                px: 2,
                py: 1.25,
                minWidth: 80,
                minHeight: 44,
                fontWeight: 500,
                '&.Mui-selected': {
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
              },
            }}
          >
            <ToggleButton value="active" aria-label={t('flocks.active')}>
              {t('flocks.active')}
            </ToggleButton>
            <ToggleButton value="all" aria-label={t('common.all')}>
              {t('common.all')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {isLoading ? (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2, 3].map((index) => (
              <FlockCardSkeleton key={index} />
            ))}
          </Box>
        ) : filteredFlocks.length === 0 ? (
          <FlocksEmptyState onAddClick={() => setIsCreateModalOpen(true)} />
        ) : (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filteredFlocks.map((flock) => (
              <FlockCard
                key={flock.id}
                flock={flock}
                coopName={coop?.name || ''}
                onEdit={handleEdit}
                onArchive={handleArchive}
              />
            ))}
          </Box>
        )}

        {isRefreshing && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mt: 2,
              py: 2,
            }}
          >
            <CircularProgress size={24} thickness={4} />
            <Typography variant="caption" color="text.secondary">
              {t('common.loading')}
            </Typography>
          </Box>
        )}
      </Box>

      <Fab
        color="primary"
        aria-label={t('flocks.addFlock')}
        data-testid="add-flock-fab"
        sx={{
          position: 'fixed',
          bottom: { xs: 'calc(env(safe-area-inset-bottom) + 80px)', sm: 24 },
          right: 16,
        }}
        onClick={() => setIsCreateModalOpen(true)}
      >
        <AddIcon />
      </Fab>

      <CreateFlockModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        coopId={coopId}
      />

      {selectedFlock && (
        <EditFlockModal
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          flock={selectedFlock}
        />
      )}

      {selectedFlock && (
        <ArchiveFlockDialog
          open={isArchiveDialogOpen}
          onClose={handleCloseArchiveDialog}
          onConfirm={handleConfirmArchive}
          flockIdentifier={selectedFlock.identifier}
          isPending={isArchiving}
        />
      )}
    </Container>
  );
}
